import { Ref, useEffect, useImperativeHandle, useRef, useState } from "react"
import { DateRange, Modifiers, MonthChangeEventHandler, PropsRange } from "react-day-picker"
import { isBefore, isValid, isWithinInterval } from "date-fns"
import { t } from "i18next"
import { Calendar, CalendarFooter } from "@/components/ui/calendar"
import { DateInput, DatePickerFooter } from "@/components/ui/date-picker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { DateRangeInputProvider, useDateRangeInputContext } from "@/contexts/date-range-input/date-range-input-context"
import { useClickOutside } from "@/hooks/useClickOutside"
import { formatDateRangeInput, formatDateRangeWithDot, getDatesFromRange } from "@/lib/format-date"

interface DateRangeInputProps extends React.HTMLAttributes<HTMLInputElement> {
  label?: string
  placeholder?: string
  onSelectDays?: (
    range: DateRange | undefined,
    selectedDay?: Date,
    activeModifiers?: Modifiers,
    e?: React.MouseEvent | React.KeyboardEvent,
  ) => void
  shortcuts?: React.ReactNode
  today?: Date
  defaultMonth?: Date
  onMonthChange?: MonthChangeEventHandler
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>, selectedRange?: DateRange) => void
  error?: string
  translateError?: boolean
  required?: boolean
  disabledMatcher?: (date: Date) => boolean
  value?: DateRange
}

interface CustomInputHandle {
  focus: () => void
}

const CustomCalendarFooter = () => {
  const { onClear } = useDateRangeInputContext()
  return (
    <DatePickerFooter>
      <CalendarFooter onClick={onClear} />
    </DatePickerFooter>
  )
}

export const DateRangeInput = (props: DateRangeInputProps & { ref?: Ref<CustomInputHandle> }) => {
  const {
    today = new Date(),
    onSelectDays,
    shortcuts,
    label = t("patient.sickLeaveCertificate.timeFrame"),
    placeholder = "DD.MM.YYYY - DD.MM.YYYY",
    className,
    defaultMonth,
    onMonthChange,
    onInputChange,
    error,
    translateError,
    required,
    disabledMatcher,
    value,
    ref,
    ...rest
  } = props

  const defaultRange: DateRange = { from: today }
  const defaultDate = defaultRange.from
  const defaultInput = formatDateRangeInput(value ?? defaultRange)
  const [hoveringDate, setHoveringDate] = useState<Date | undefined>(undefined)
  const [isFocused, setIsFocused] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [month, setMonth] = useState<Date | undefined>(defaultMonth ?? defaultDate)
  const [inputValue, setInputValue] = useState<string>(defaultInput)
  const [inputError, setInputError] = useState("")
  const [selectedRange, setSelectedRange] = useState<DateRange>(defaultRange)

  const popoverContentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const calendarIconRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (value) {
      setInputValue(formatDateRangeInput(value))
      setSelectedRange(value)
    }
  }, [value])

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus()
    },
  }))

  useClickOutside((event: MouseEvent) => {
    if (
      popoverContentRef.current &&
      !popoverContentRef.current.contains(event.target as HTMLElement) &&
      calendarIconRef.current &&
      !calendarIconRef.current.contains(event.target as HTMLElement)
    ) {
      setIsOpen(false)
      handleUpdateData(inputValue)
    }
  }, inputRef)

  const handleCloseCalendar = () => {
    setIsOpen(false)
    if (inputRef?.current) {
      inputRef.current.blur()
    }
  }

  const handleSelectDays: PropsRange["onSelect"] = (range, selectedDate, ...rest) => {
    let selectingRange = range
    const isSelectedDateBefore = selectedRange?.from && isBefore(selectedDate, selectedRange.from)
    if (isSelectedDateBefore || selectedRange?.to) {
      selectingRange = { from: selectedDate, to: undefined }
    }
    const value = formatDateRangeInput(selectingRange)
    setInputValue(value)

    if (selectingRange?.to) {
      setIsOpen(false)
      checkInputError(value)
    }
    setSelectedRange(selectingRange ?? defaultRange)
    onSelectDays?.(selectingRange, selectedDate, ...rest)
  }

  const handleMonthChange: MonthChangeEventHandler = (month) => {
    setMonth(month)
    onMonthChange?.(month)
  }

  const checkInputError = (value: string) => {
    const { fromDate, toDate } = getDatesFromRange(value)
    const [, toDateText] = value.split("-")

    if (!isValid(fromDate) || (toDateText && !isValid(toDate)) || isBefore(toDate, fromDate)) {
      setInputError("common.fieldErrorList.invalidDateRange")
      return true
    }

    setInputError("")
    return false
  }

  const handleCalendarIconClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setIsOpen((isOpen) => !isOpen)
    setIsFocused(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart: cursorPosition } = e.target
    const formattedValue = formatDateRangeWithDot(value, cursorPosition === value.length)
    setInputValue(formattedValue)

    if (!value || checkInputError(value)) {
      setSelectedRange(defaultRange)
      onInputChange?.(e)
      return
    }

    const { fromDate, toDate } = getDatesFromRange(value)
    const range = defaultRange

    if (isValid(fromDate)) {
      range.from = fromDate
      handleMonthChange(fromDate)
    }
    if (isValid(toDate) && !isBefore(toDate, fromDate)) {
      range.to = toDate
    }
    setSelectedRange(range)
    onInputChange?.(e, range)
  }

  const handleReset = () => {
    onSelectDays?.(defaultRange)
    setSelectedRange(defaultRange)
    setMonth(defaultDate)
    setInputValue(defaultInput)
  }

  const handleUpdateData = (value: string) => {
    if (!value) {
      handleReset()
      checkInputError(defaultInput)
    }
  }

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (!isOpen) setIsOpen(true)

    const { fromDate } = getDatesFromRange(e.target.value)
    if (isValid(fromDate)) {
      setMonth(fromDate)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Tab") {
      handleUpdateData(inputValue)
      handleCloseCalendar()
    }
    if (e.key === "Backspace") {
      const { value, selectionStart } = e.currentTarget
      const { length } = value

      // Remove the trailing hyphen or dot
      if (
        selectionStart === length &&
        (length === 3 || length === 6 || length === 11 || length === 14 || length === 17)
      ) {
        const input = value.substring(0, length - 1)
        setInputValue(input)
        return
      }
    }
  }

  const isWithinRange = (date: Date) => {
    if (selectedRange?.from && hoveringDate) {
      const startDate = isBefore(selectedRange.from, hoveringDate) ? selectedRange.from : hoveringDate
      const endDate = isBefore(selectedRange.from, hoveringDate) ? hoveringDate : selectedRange.from
      return isWithinInterval(date, { start: startDate, end: endDate })
    }
    return false
  }

  const resetHoveringDate = () => {
    setHoveringDate(undefined)
  }

  useEffect(() => {
    setIsFocused(isOpen)
  }, [isOpen])

  useEffect(() => {
    if (!isFocused) return

    const calendarBody = document.querySelector(".js-sofia-rdp-tbody")

    if (!calendarBody) return

    calendarBody.addEventListener("mouseleave", resetHoveringDate)

    return () => {
      calendarBody.removeEventListener("mouseleave", resetHoveringDate)
    }
  }, [isFocused])

  return (
    <DateRangeInputProvider onClear={handleReset}>
      <Popover open={isOpen}>
        <PopoverTrigger asChild>
          <DateInput
            className={className}
            value={inputValue}
            label={label}
            focus={isFocused}
            type="text"
            inputRef={inputRef}
            calendarIconRef={calendarIconRef}
            readOnly={false}
            placeholder={placeholder}
            onChange={handleInputChange}
            onCalendarIconClick={handleCalendarIconClick}
            onFocus={handleFocus}
            error={error ?? inputError}
            translateError={translateError}
            required={required}
            onKeyDown={handleKeyDown}
            maxLength={21}
            {...rest}
          />
        </PopoverTrigger>

        <PopoverContent
          sideOffset={0}
          className="bg-surface w-auto rounded-lg p-0"
          align="start"
          ref={popoverContentRef}
          onOpenAutoFocus={(e: Event) => {
            if (document.activeElement === inputRef.current) e.preventDefault()
          }}
          onCloseAutoFocus={(e: Event) => e.preventDefault()}
          onEscapeKeyDown={handleCloseCalendar}>
          {shortcuts && <div className="px-2 py-4">{shortcuts}</div>}
          <Calendar
            mode="range"
            today={today}
            onSelect={handleSelectDays}
            selected={selectedRange}
            onDayMouseEnter={setHoveringDate}
            disabledMatcher={disabledMatcher}
            modifiers={{
              "rdp-in-selecting": (date: Date) => {
                return isWithinRange(date) && !selectedRange?.to
              },
            }}
            components={{
              Footer: CustomCalendarFooter,
            }}
            month={month}
            onMonthChange={handleMonthChange}
          />
        </PopoverContent>
      </Popover>
    </DateRangeInputProvider>
  )
}
